import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import jwtDecode from 'jwt-decode';

import { AuthorizeRequest, AuthorizeResponse } from '../api/models/Authorize';

import { Authorize, revokeToken } from '../api/services/authorization';
import {
  getAccessTokenFromLocalStorage,
  removeAuthTokenValuesFromLocalStorage,
  setAuthTokenValuesInLocalStorage,
} from '../utils/localStorageAuthHelper';

import { SliceCallState } from './sliceCallState';

export interface TokenData {
  exp?: number;
  dashboard: boolean;
  userId: number;
  userType: number
  features: {
    REPORT_CATEGORIES: {
      permissions: string[];
    };
    REPORTS: {
      permissions: string[];
    };
  };
}
export interface AuthState {
  token?: AuthorizeResponse;
  error?: SerializedError;
  decodedToken: TokenData;
  loading: SliceCallState;
}

export interface AuthAction {
  payload: AuthorizeResponse & { error?: SerializedError };
  type: string;
}

const initialState: AuthState = {
  token: undefined,
  decodedToken: {
    exp: undefined,
    dashboard: true,
    userId: 0,
    userType: 0,
    features: {
      REPORT_CATEGORIES: {
        permissions: [],
      },
      REPORTS: {
        permissions: [],
      },
    },
  },
  loading: SliceCallState.pending,
};

export const authorize = createAsyncThunk<AuthorizeResponse, AuthorizeRequest, { rejectValue: SerializedError }>(
  'api/authorize',
  async (authData: AuthorizeRequest, thunkAPI) => {
    try {
      return await Authorize(authData);
    } catch (error) {
      const err = error as SerializedError;
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const logout = createAsyncThunk('api/logout', async (_, thunkAPI) => {
  try {
    return await revokeToken();
  } catch (error) {
    const err = error as SerializedError;
    return thunkAPI.rejectWithValue(err);
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    getDataFromLocalStorage: (state: AuthState) => {
      const token = getAccessTokenFromLocalStorage();
      state.token = token;
      state.decodedToken = token ? jwtDecode(token.access_token) : state.decodedToken;
      state.loading = SliceCallState.idle;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authorize.pending, (state: AuthState) => {
      state.loading = SliceCallState.pending;
    });

    builder.addCase(authorize.fulfilled, (state: AuthState, action: AuthAction) => {
      state.token = action.payload;
      state.loading = SliceCallState.succeeded;
      state.decodedToken = jwtDecode(action.payload.access_token);

      setAuthTokenValuesInLocalStorage(action.payload);
    });

    builder.addCase(authorize.rejected, (state, action) => {
      state.loading = SliceCallState.failed;
      state.error = action.payload;
      throw new Error(action.payload?.message);
    });

    builder.addCase(logout.pending, (state) => {
      state.loading = SliceCallState.pending;
    });

    builder.addCase(logout.fulfilled, (state: AuthState) => {
      state.token = undefined;
      state.decodedToken = initialState.decodedToken;
      state.loading = SliceCallState.succeeded;
      removeAuthTokenValuesFromLocalStorage();
    });

    builder.addCase(logout.rejected, (state: AuthState, action) => {
      state.loading = SliceCallState.failed;
      state.token = undefined;
      state.decodedToken = initialState.decodedToken;
      removeAuthTokenValuesFromLocalStorage();
      throw new Error(action.error?.message);
    });
  },
});

export const actions = authSlice.actions;
